import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { hydratedGlobalParams, normalizeFilterParams } from '../../../shared/workspace-element-context';

@Injectable({
  providedIn: 'root'
})
export class PartnerAllocNewService {

  constructor(
    private http: HttpClient,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  private getGlobalParams(): any {
    return hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
  }

  private getFilterParams(): any {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    return normalizeFilterParams(activeScreen);
  }

  private getUrlEncodedHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
  }

  private toUrlEncoded(params: any): string {
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach((key: string) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });
    return urlEncoded.toString();
  }

  private cleanParams(params: any): any {
    const cleaned: any = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        cleaned[key] = params[key];
      }
    });
    return cleaned;
  }

  private loadJsonObject(baseUrl: string, params: any): Observable<any> {
    const globalParams = this.getGlobalParams();
    const filterParams = this.getFilterParams();
    const mergedParams = _.merge({}, globalParams, filterParams, this.cleanParams(params));
    delete mergedParams.checkLockedbj;

    return this.http.get<any>(`${baseUrl}/loadJsonObject`, {
      params: mergedParams,
      withCredentials: true
    }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1') {
          return response;
        }
        throw new Error(response.errorMessage || 'Failed to load data');
      }),
      catchError((error) => throwError(error))
    );
  }

  // K1 Tab: Load allocation form details (action_code: 36djph)
  loadK1FormDetails(baseUrl: string, rowData: any, ssoId: string): Observable<any> {
    const params: any = {
      action_code: '36djph',
      section_name: '',
      leid: rowData.PSHIP_LEID,
      cdr_no: rowData.PSHIP_TAX_CODE,
      rp: rowData.PSHIP_REPORTING_PERIOD,
      bsla: rowData.PSHIP_BUSINESS,
      curr: rowData.PSHIP_CURRENCY,
      ptr_leid: rowData.POSTING_PARTNER_LEID,
      ptr_cdr_no: rowData.POSTING_PARTNER_TAX_CODE,
      ptr_rp: rowData.POSTING_PARTNER_REPORTING_PERIOD,
      ptr_bsla: rowData.POSTING_PARTNER_BUSINESS,
      ptr_curr: rowData.POSTING_PARTNER_CURRENCY,
      sso_id: ssoId,
      cc_key: rowData.COMBINATION_KEY,
      ptr_cc_key: rowData.PTR_COMBINATION_KEY
    };
    return this.loadJsonObject(baseUrl, params);
  }

  // K1 Tab: Load issue details (action_code: 68yejg)
  loadIssueDetails(baseUrl: string, taxYear: string, scenario: string, jcdKey: string, combinationKey: string, ptrCombinationKey: string): Observable<any> {
    const params: any = {
      action_code: '68yejg',
      combination_key: combinationKey,
      ptr_combination_key: ptrCombinationKey
    };
    if (taxYear) params.tax_year = taxYear;
    if (scenario) params.scenario = scenario;
    if (jcdKey) params.jcd_key = jcdKey;
    return this.loadJsonObject(baseUrl, params);
  }

  // K1 Tab: Load issue dropdown (action_code: t748jd)
  loadIssueDropdown(baseUrl: string, taxYear: string, scenario: string, jcdKey: string): Observable<any> {
    const params: any = { action_code: 't748jd' };
    if (taxYear) params.tax_year = taxYear;
    if (scenario) params.scenario = scenario;
    if (jcdKey) params.jcd_key = jcdKey;
    return this.loadJsonObject(baseUrl, params);
  }

  // K1 Tab: Load all dropdowns (action_code: go3irt)
  loadAllDropdowns(baseUrl: string, taxYear: string): Observable<any> {
    const params: any = { action_code: 'go3irt' };
    if (taxYear) params.tax_year = taxYear;
    return this.loadJsonObject(baseUrl, params);
  }

  // K1 Tab: Load formula details (action_code: gb8qgn)
  loadFormFormulaDetails(baseUrl: string, taxYear: string, scenario: string, partNo: string, sectionName: string): Observable<any> {
    const params: any = {
      action_code: 'gb8qgn',
      part_no: partNo,
      section_name: sectionName
    };
    if (taxYear) params.tax_year = taxYear;
    if (scenario) params.scenario = scenario;
    return this.loadJsonObject(baseUrl, params);
  }

  // SK3 Tab: Load SK3 details (action_code: fp23ed)
  loadSK3Details(baseUrl: string, taxYear: string, scenario: string, jcdKey: string, combinationKey: string, ptrCombinationKey: string): Observable<any> {
    const params: any = {
      action_code: 'fp23ed',
      pship_cc_key: combinationKey,
      ptr_cc_key: ptrCombinationKey
    };
    if (taxYear) params.tax_year = taxYear;
    if (scenario) params.scenario = scenario;
    if (jcdKey) params.jcd_key = jcdKey;
    return this.loadJsonObject(baseUrl, params);
  }

  // SK3 Tab: Load SK3 attachment details (action_code: uqnczz)
  loadSK3AttachmentDetails(baseUrl: string, taxYear: string, scenario: string, jcdKey: string, combinationKey: string, ptrCombinationKey: string, formKey: string, scheduleName: string, ssoId: string): Observable<any> {
    const params: any = {
      action_code: 'uqnczz',
      pship_cc_key: combinationKey,
      ptr_cc_key: ptrCombinationKey,
      form_key: formKey,
      schedule_name: scheduleName,
      sso_id: ssoId
    };
    if (taxYear) params.tax_year = taxYear;
    if (scenario) params.scenario = scenario;
    if (jcdKey) params.jcd_key = jcdKey;
    return this.loadJsonObject(baseUrl, params);
  }

  // SK3 Tab: Load SK3 formula details (action_code: 6v54vu)
  loadSK3FormulaDetails(baseUrl: string, taxYear: string, scenario: string, jcdKey: string, partNo: string, sectionName: string): Observable<any> {
    const params: any = {
      action_code: '6v54vu',
      part_no: partNo,
      section_name: sectionName
    };
    if (taxYear) params.tax_year = taxYear;
    if (scenario) params.scenario = scenario;
    if (jcdKey) params.jcd_key = jcdKey;
    return this.loadJsonObject(baseUrl, params);
  }

  // Lookups: Load country code list (action_code: 7mswb0)
  loadCountryCodeList(baseUrl: string, taxYear: string, ssoId: string): Observable<any> {
    const params = { action_code: '7mswb0', tax_year: taxYear, sso_id: ssoId };
    return this.loadJsonObject(baseUrl, params);
  }

  // Lookups: Load SIC codes (action_code: yzpcur)
  loadSICCodes(baseUrl: string, taxYear: string): Observable<any> {
    const params = { action_code: 'yzpcur', tax_year: taxYear };
    return this.loadJsonObject(baseUrl, params);
  }

  // Lookups: Get country list (action_code: hs9y95)
  getCountryList(baseUrl: string, taxYear: string, ssoId: string): Observable<any> {
    const params = { action_code: 'hs9y95', tax_year: taxYear, sso_id: ssoId };
    return this.loadJsonObject(baseUrl, params);
  }

  // Lookups: Get tax types (action_code: x4ulzf)
  getTaxTypes(baseUrl: string, taxYear: string): Observable<any> {
    const params = { action_code: 'x4ulzf', tax_year: taxYear };
    return this.loadJsonObject(baseUrl, params);
  }

  // Lookups: Get currency codes (action_code: 2dokld)
  getCurrencyCodes(baseUrl: string, taxYear: string): Observable<any> {
    const params = { action_code: '2dokld', tax_year: taxYear };
    return this.loadJsonObject(baseUrl, params);
  }

  // Lookups: Get category codes (action_code: sner68)
  getCategoryCodes(baseUrl: string, taxYear: string): Observable<any> {
    const params = { action_code: 'sner68', tax_year: taxYear };
    return this.loadJsonObject(baseUrl, params);
  }

  // Lookups: Get default category (action_code: gmxuns)
  getDefaultCategory(baseUrl: string, taxYear: string): Observable<any> {
    const params = { action_code: 'gmxuns', tax_year: taxYear };
    return this.loadJsonObject(baseUrl, params);
  }

  // Lookups: Get default country code (action_code: 7rjgms)
  getDefaultCountryCode(baseUrl: string, taxYear: string): Observable<any> {
    const params = { action_code: '7rjgms', tax_year: taxYear };
    return this.loadJsonObject(baseUrl, params);
  }

  // Attachments: Load attachment files by file key set
  loadAttachmentsByFileKeySet(adminUrl: string, fileKeySet: string): Observable<any> {
    return this.http.post<any>(
      `${adminUrl}/loadAttachmentsByFileKeySet`,
      { fileKeySet },
      { withCredentials: true }
    );
  }

  // Attachments: Upload K3 attachment file (multipart, flow.js-compatible)
  uploadK3Attachment(adminUrl: string, file: File, queryParams: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('flowChunkNumber', '1');
    formData.append('flowChunkSize', file.size.toString());
    formData.append('flowCurrentChunkSize', file.size.toString());
    formData.append('flowTotalSize', file.size.toString());
    formData.append('flowIdentifier', `${file.size}-${file.name.replace(/\./g, '')}`);
    formData.append('flowFilename', file.name);
    formData.append('flowRelativePath', file.name);
    formData.append('flowTotalChunks', '1');

    const urlParams = new URLSearchParams();
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== null) {
        urlParams.set(key, queryParams[key].toString());
      }
    });

    return this.http.post(
      `${adminUrl}/uploadK3Attachment?${urlParams.toString()}`,
      formData,
      { withCredentials: true, responseType: 'text' }
    ).pipe(
      map((text: string) => {
        try { return JSON.parse(text); } catch { return { uploadStatus: 'success' }; }
      })
    );
  }

  // Attachments: Delete stored attachment
  deleteStoredAttachment(adminUrl: string, fileName: string, fileKey: string): Observable<any> {
    return this.http.post<any>(
      `${adminUrl}/deleteStoredAttachment`,
      { destDir: 'temp', flowFilename: fileName, fileKey },
      { withCredentials: true }
    );
  }

  // Attachments: Get API auth token for download
  getAPIAuthToken(baseUrl: string): Observable<any> {
    return this.http.get<any>(`${baseUrl}/getAPIAuthToken`, { withCredentials: true });
  }

  // Save: Save partner allocation form (action_code: oofs04)
  savePartnerAllocationForm(
    baseUrl: string,
    returnClobSettingsObj: any,
    formSaveDtls: any[]
  ): Observable<any> {
    const globalParams = this.getGlobalParams();
    const filterParams = this.getFilterParams();

    const jsonObj = JSON.stringify(formSaveDtls);
    returnClobSettingsObj['jcd_key'] = globalParams.jcd_key;
    const jsonSettings = JSON.stringify(returnClobSettingsObj);

    let params: any = _.merge({}, globalParams, filterParams);
    params.clob_data = jsonObj;
    params.clob_settings = jsonSettings;
    params.process_name = 'Partner Allocation Setup New';
    params.jcd_key = globalParams.jcd_key;
    params.sso_id = returnClobSettingsObj.sso_id;

    console.log('[SAVE SERVICE] params:', params);
    console.log('[SAVE SERVICE] clob_data:', jsonObj);
    console.log('[SAVE SERVICE] clob_settings:', jsonSettings);

    return this.http.post<any>(
      `${baseUrl}/saveJsonObject?action_code=oofs04`,
      this.toUrlEncoded(params),
      { headers: this.getUrlEncodedHeaders(), withCredentials: true }
    );
  }
}
